import {inject, Injectable, input, signal, WritableSignal} from '@angular/core';
import {
    confirmResetPassword,
    ConfirmResetPasswordInput,
    confirmSignUp,
    ConfirmSignUpInput,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resetPassword,
    signIn,
    SignInInput,
    signOut,
    signUp,
    updatePassword,
    UpdatePasswordInput,
    updateUserAttributes,
    confirmUserAttribute,
    ConfirmUserAttributeInput,
    sendUserAttributeVerificationCode,
    VerifiableUserAttributeKey
} from 'aws-amplify/auth';
import {BehaviorSubject, Observable} from "rxjs";
import {HttpHeaders} from "@angular/common/http";
import {CanActivateFn, Router} from "@angular/router";
import {Project} from "../api/db/entities/Project";
import {
    BetaReaderMatchUse,
    BetaReaderStatus,
    PlatformRole,
    SecureRole,
    WriterStatus
} from "../api/enums";
import BadWordsFilter from "bad-words";
import {v4 as uuid} from "uuid";
import {ErrorService} from "./error.service";
import {ProgressService} from "./progress.service";
import {GatherDebugData} from "./gather-debug-data";
import {MessageService} from "primeng/api";
import {DocumentMetadata} from "../components/contents-management/MetadataBlot";
import {DataSource} from "typeorm";
import {BetaReaderProfile} from "../api/db/entities/BetaReaderProfile";
import {BetaReaderProject} from "../api/db/entities/BetaReaderProject";
import {PlatformUser} from "../api/db/entities/PlatformUser";
import {WriterProfile} from "../api/db/entities/WriterProfile";
import { AwardedBadges } from '../api/db/entities/AwardedBadges';

import {toObservable} from "@angular/core/rxjs-interop";
import {ProjectSchemaDisplay, ProjectSchemas} from "../api/project_schemas";
import {ORM} from "../utils";
import {ProjectProfileGroup} from "../api/db/entities/ProjectProfileGroup";
import { ProjectProfileSelection } from "../api/db/entities/ProjectProfileSelection";
import { FICTION_SERIES } from '../api/schemas/FICTION_SERIES';
import { ProjectMatchStatus } from '../api/enums';
import { ProjectMatchType } from '../api/db/entities/ProjectMatchType';


@Injectable({
    providedIn: 'root',
})
export class DataService implements GatherDebugData {
    private messageService = inject(MessageService);
    private progressService = inject(ProgressService);
    private router = inject(Router);
    private errorService = inject(ErrorService);

    readonly STORAGE_KEY_PLATFORM_USER : string = 'platform_user_data_v1.1';
    readonly STORAGE_KEY_PU_BETA_READER : string = 'pu_beta_reader_data_V1.0';
    readonly STORAGE_KEY_PU_WRITER : string = 'pu_writer_data_V1.0';

    private dataStore: {
        user: PlatformUser | null,
        betaReader: BetaReaderProfile | null,
        writerProjects: Project[] | null,
        betaReaderProjects: { betaReaderProject: BetaReaderProject, project: Project }[] | null,
        writer: WriterProfile | null,
        betaMatches: ProjectMatchType[],
        projectResourceMatches: Record<string, WriterResourceMatchType>,
        hydrationUUID: string | null,
        readerMatchedProjects: Project[] | null,
        writerMatches: ProjectMatchType[]
    } = {
        user: null,
        betaReader: null,
        writer: null,
        writerProjects: null,
        betaReaderProjects: null,
        betaMatches: [],
        projectResourceMatches: {},
        hydrationUUID: null,
        readerMatchedProjects: null,
        writerMatches: []
    };

    public user: WritableSignal<PlatformUser> = signal(null);
    public rMatchSignal: WritableSignal<ProjectMatchType[]> = signal(null);
    public wMatchSignal: WritableSignal<ProjectMatchType[]> = signal(null);
    public static s_user: PlatformUser = null;
    public readonly userObs: Observable<PlatformUser> = toObservable(this.user);

    private _betaReader: BehaviorSubject<BetaReaderProfile> = new BehaviorSubject(this.dataStore.betaReader);
    public readonly betaReader: Observable<BetaReaderProfile> = this._betaReader.asObservable();
    private _writer: BehaviorSubject<WriterProfile> = new BehaviorSubject(this.dataStore.writer);
    public readonly writer: Observable<WriterProfile> = this._writer.asObservable();
    private _betaReaderProjects: BehaviorSubject<{
        betaReaderProject: BetaReaderProject,
        project: Project
    }[]> = new BehaviorSubject(this.dataStore.betaReaderProjects);
    public readonly betaReaderProjects: Observable<{
        betaReaderProject: BetaReaderProject,
        project: Project
    }[]> = this._betaReaderProjects.asObservable();
    private _writerProjects: BehaviorSubject<Project[]> = new BehaviorSubject(this.dataStore.writerProjects);
    public readonly writerProjects: Observable<Project[]> = this._writerProjects.asObservable();

    public loggedIn: WritableSignal<boolean> = signal(false);
    public started: WritableSignal<boolean> = signal(false);

    private secureRoles: string[] = [];

    private badWords = new BadWordsFilter();

    redirectUrl: string;

    public selectedProject: Project | BetaReaderProject;
    public coreProject: Project;
    public currentSchema: ProjectSchemaDisplay;

    tableOfContents: WritableSignal<DocumentMetadata> = signal(null); // Filled on editorCreate
    private dataSource: DataSource;

    private fetchLock: boolean = false;

    constructor() {
        const errorService = this.errorService;

        errorService.registerServiceForDebug('DataService', this);
    }

    getDebugInfo() {
        return {
            hydrationUUID: this.dataStore.hydrationUUID,
            user: this.dataStore.user,
            betaReader: this.dataStore.betaReader,
            writer: this.dataStore.writer,
            writerProjects: this.dataStore.writerProjects,
            betaReaderProject: this.dataStore.writerProjects,
            betaMatches: this.dataStore.betaMatches,
            projectResourceMatches: this.dataStore.projectResourceMatches,
        };
    }

    // public fillOriginalValues(namespace: string, ps: ProjectProfileGroup) {
    //     this.pdOriginalValues[namespace] = {};
    //     this.pdOriginalSingleValues[namespace] = {};
    //
    //     for (const sel of ps) {
    //         this.pdOriginalValues[namespace][sel.categoryUniqueId] = sel.value;
    //         if (sel.value === 'true') {
    //             const cat = ProjectSchemas.getCategoryByUniqueId(this.currentSchema.code, sel.categoryUniqueId);
    //             if (!cat) {
    //                 // console.log('PROFILE DESCRIPTOR REMOVED:', sel);
    //             } else {
    //                 this.pdOriginalSingleValues[namespace][cat.uniqueId] = sel.descriptorUniqueId;
    //             }
    //         }
    //     }
    // }
    //
    // private findCategoryIdForDescriptorId(descId: string): ProjectDescriptorCategory {
    //     const catId: string = descId.split('|')[0]; //Everything to the first | is the category id
    //     return this.currentSchema.projectDescriptorCategories.find(c => {
    //         return c.uniqueId === catId;
    //     });
    // }

    // async saveBetaReaderTempToProfile(betaReaderProfile: BetaReaderProfile, projectUUID: string) {
    //     try {
    //         this.progressService.blockWithMessage('Saving Profile');
    //         const pde = betaReaderProfile.projectDescriptorSelections ?
    //             betaReaderProfile.projectDescriptorSelections : [];
    //         try {
    //             let updates: {
    //                 id: number,
    //                 projectDescriptorId: number,
    //                 betaReaderProfileId: number,
    //                 value: string
    //             }[] = [];
    //             let creates: { betaReaderProfileId: number, projectDescriptorId: number, value: string }[] = [];
    //             const values = this.getPDCurrentValues(projectUUID);
    //             console.log(values);
    //             for (const keyS of Object.keys(values)) {
    //                 const key: number = Number.parseInt(keyS,10);
    //                 // console.log('VALUES', key, values.get(key), values);
    //                 let found = false;
    //                 for (const pdeEntry of pde) {
    //                     if (pdeEntry.projectDescriptorId === key) {
    //                         pdeEntry.value = values[key];
    //                         found = true;
    //                         updates.push({
    //                             id: pdeEntry.id,
    //                             projectDescriptorId: pdeEntry.projectDescriptorId,
    //                             betaReaderProfileId: betaReaderProfile.id,
    //                             value: values[key]
    //                         });
    //                         break;
    //                     }
    //                 }
    //                 if (!found) {
    //                     creates.push({
    //                         value: values[key],
    //                         betaReaderProfileId: betaReaderProfile.id,
    //                         projectDescriptorId: key
    //                     });
    //                 }
    //             }
    //
    //             for (const u of updates) {
    //                 await this.dataSource.getRepository(BetaReaderProfileSelection).update(u.id,
    //                     {
    //                         projectDescriptorId: u.projectDescriptorId,
    //                         betaReaderProfileId: u.betaReaderProfileId,
    //                         value: u.value
    //                     }
    //                 );
    //             }
    //
    //             for (const c of creates) {
    //                 await this.dataSource.getRepository(BetaReaderProfileSelection).insert(c);
    //             }
    //             await this.getOrCreatePlatformUser();
    //         } catch (e) {
    //             this.errorService.handleError({
    //                 error: e,
    //                 stack: e.stack,
    //                 severity: 'error',
    //                 summary: 'Error',
    //                 detail: 'Unable to update beta reader profile. See log for details'
    //             });
    //         }
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }

    // async refreshDescriptorOptionData() {
    //     //This is cached - if it isn't cached locally - go get it remotely.
    //
    //     //Get it locally and just use it if it isn't too old - too old is 15 minutes.
    //     //Too old or not available? Check if the version has changed on the remote cache.
    //     //    - Not changed? Get it locally and reset the refresh date.
    //     //Newer version - store locally
    //     //return
    //     let dodObj: ProjectDescriptorCategory[];
    //
    //     dodObj = await this.dataSource.getRepository(ProjectDescriptorCategory)
    //         .createQueryBuilder("pdc")
    //         .leftJoinAndSelect("pdc.variants", "pdcv")
    //         .leftJoinAndSelect("pdc.descriptors", "pd")
    //         .where("pdc.deleted_at IS NULL AND pd.deleted_at IS NULL")
    //         .orderBy("pd.display_order")
    //         .cache(JSON.stringify({
    //                 scope: 'GLOBAL', //This is a global cache - it is shared across all users - the other option is LOCAL
    //                 name: this.STORAGE_KEY_DESCRIPTOR_OPTION_DATA,
    //                 timeToLiveSeconds: 24*60*60, //One day before it is even checked remotely
    //         }))
    //         .getMany();
    //
    //     this.dataStore.projectDescriptorCategories = dodObj;
    //     this._projectDescriptorCategories.next(this.dataStore.projectDescriptorCategories);
    // }
    //
    // async removeDescriptor(pd: ProjectDescriptor) {
    //     try {
    //         this.progressService.blockWithMessage('Removing Descriptor');
    //         pd.deletedAt = new Date();
    //         pd.deletedBy = await this.getUserId();
    //         console.log("PD", pd);
    //         const result = await this.dataSource.getRepository(ProjectDescriptor)
    //             .update({id: pd.id}, {
    //                 deletedAt: pd.deletedAt,
    //                 deletedBy: pd.deletedBy
    //             });
    //         console.log(result);
    //         await this.refreshDescriptorOptionData();
    //     } catch (error) {
    //         console.log(error);
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error removing item',
    //             detail: 'Unable to mark item as deleted. Please check log for details.',
    //             error: error,
    //             stack: error.stack,
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async saveDescriptor(pd: ProjectDescriptor) {
    //     try {
    //         this.progressService.blockWithMessage('Saving Changes to Descriptor');
    //         const updateResult = await this.dataSource.getRepository(ProjectDescriptor)
    //             .update({id: pd.id}, {
    //                 displayOrder: pd.displayOrder,
    //                 enUsHelp: pd.enUsHelp,
    //                 enUsLabel: pd.enUsLabel
    //             });
    //         await this.refreshDescriptorOptionData();
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating item',
    //             detail: 'Unable to update item. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async updateVariant(variant: ProjectDescriptorCategoryVariant) {
    //     try {
    //         this.progressService.blockWithMessage('Updating Variant');
    //         const updateResult = await this.dataSource.getRepository(ProjectDescriptorCategoryVariant)
    //             .update({id: variant.id}, {
    //                 categoryId: variant.categoryId,
    //                 uses: variant.uses,
    //                 enUsHelp: variant.enUsHelp,
    //                 enUsLabel: variant.enUsLabel,
    //                 questionType: variant.questionType,
    //                 tagIcon: variant.tagIcon,
    //                 tagColor: variant.tagColor
    //             });
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating category',
    //             detail: 'Unable to update Variant. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async createVariant(variant: ProjectDescriptorCategoryVariant) {
    //     try {
    //         this.progressService.blockWithMessage('Creating Variant');
    //         const createResult = await this.dataSource.getRepository(ProjectDescriptorCategoryVariant)
    //             .insert(variant);
    //         variant.id = createResult.identifiers[0].id;
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating category',
    //             detail: 'Unable to update category. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async removeVariant(variant: ProjectDescriptorCategoryVariant) {
    //     try {
    //         this.progressService.blockWithMessage('Removing Variant');
    //         await this.dataSource.getRepository(ProjectDescriptorCategoryVariant)
    //             .delete({id: variant.id});
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating category',
    //             detail: 'Unable to remove variant. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async saveCategory(pdc: ProjectDescriptorCategory) {
    //     let updateResult;
    //     try {
    //         this.progressService.blockWithMessage('Saving Changes to Category');
    //         updateResult = await this.dataSource.getRepository(ProjectDescriptorCategory)
    //             .update({id: pdc.id}, {
    //                 uses: pdc.uses,
    //                 displayOrder: pdc.displayOrder,
    //                 betaReaderMatchUse: pdc.betaReaderMatchUse
    //             });
    //
    //         for (let variant of pdc.variants) {
    //             if (variant.id) {
    //                 await this.updateVariant(variant);
    //             } else {
    //                 await this.createVariant(variant);
    //             }
    //         }
    //         await this.refreshDescriptorOptionData();
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating category',
    //             detail: 'Unable to update category. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async saveCategoriesAfterReorder(categories: ProjectDescriptorCategory[]) {
    //     try {
    //         this.progressService.blockWithMessage('Saving Changes to Category Order');
    //         let i: number = 0;
    //         try {
    //             for (const pdc of categories) {
    //                 pdc.displayOrder = i++;
    //                 await this.dataSource.getRepository(ProjectDescriptorCategory)
    //                     .update({id: pdc.id}, {
    //                         uses: pdc.uses,
    //                         displayOrder: pdc.displayOrder,
    //                         betaReaderMatchUse: pdc.betaReaderMatchUse
    //                     });
    //             }
    //             await this.refreshDescriptorOptionData();
    //         } catch (error) {
    //             this.errorService.handleError({
    //                 severity: 'error',
    //                 summary: 'Error updating categories after reorder',
    //                 detail: 'Unable to update categories after reorder. Please check log for details.',
    //                 error: error,
    //                 stack: error.stack
    //             });
    //         }
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async saveAfterReorder(category: ProjectDescriptorCategory) {
    //     try {
    //         this.progressService.blockWithMessage('Saving Descriptor Order');
    //         let i: number = 0;
    //         for (const pd of category.descriptors) {
    //             pd.displayOrder = i++;
    //             if (pd.id) {
    //                 await this.dataSource.getRepository(ProjectDescriptor)
    //                     .update({id: pd.id}, {
    //                     displayOrder: pd.displayOrder,
    //                     enUsHelp: pd.enUsHelp,
    //                     enUsLabel: pd.enUsLabel,
    //                     categoryId: pd.categoryId
    //                 });
    //             } else {
    //                 await this.saveDescriptor(pd);
    //             }
    //         }
    //         await this.refreshDescriptorOptionData();
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error updating descriptors after reorder',
    //             detail: 'Unable to update descriptors after reorder. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // async addEmpty(category: ProjectDescriptorCategory): Promise<number> {
    //     try {
    //         const createResult = await this.dataSource.getRepository(ProjectDescriptor)
    //             .insert({
    //                 enUsLabel: "New Descriptor",
    //                 displayOrder: category.descriptors.length,
    //                 enUsHelp: null,
    //                 categoryId: category.id
    //             });
    //         await this.refreshDescriptorOptionData();
    //         return createResult.identifiers[0].id;
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error adding item to category',
    //             detail: 'Unable to create or save new item. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }
    //
    // async addEmptyCategory(): Promise<number> {
    //     try {
    //         const createResult = await this.dataSource.getRepository(ProjectDescriptorCategory)
    //             .insert({
    //                 displayOrder: this.dataStore.projectDescriptorCategories.length,
    //                 betaReaderMatchUse: BetaReaderMatchUse.DO_NOT_USE_TO_MATCH.valueOf()
    //             });
    //         await this.refreshDescriptorOptionData();
    //         return createResult.identifiers[0].id;
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error adding category',
    //             detail: 'Unable to create or save new category. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }
    //
    // async removeCategory(pdc: ProjectDescriptorCategory) {
    //     try {
    //         pdc.deletedAt = new Date();
    //         pdc.deletedBy = await this.getUserId();
    //         const result = await this.dataSource.getRepository(ProjectDescriptorCategory)
    //             .update({id: pdc.id}, {
    //                 deletedAt: pdc.deletedAt,
    //                 deletedBy: pdc.deletedBy
    //             });
    //         await this.refreshDescriptorOptionData();
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error removing category',
    //             detail: 'Unable to mark category as deleted. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //     }
    // }
    //
    //
    // async pullDescriptor(id: number): Promise<ProjectDescriptor> {
    //     try {
    //         return await this.dataSource.getRepository(ProjectDescriptor)
    //             .findOneBy({id: id});
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Unable to refresh descriptor',
    //             detail: 'Unable to pull descriptor from server. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }
    //
    // async pullCategory(id: number): Promise<ProjectDescriptorCategory> {
    //     try {
    //         return await this.dataSource.getRepository(ProjectDescriptorCategory)
    //             .findOneBy({id: id});
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Unable to refresh category',
    //             detail: 'Unable to pull category from server. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }
    //
    // async pullVariant(id: number): Promise<ProjectDescriptorCategoryVariant> {
    //     try {
    //         return await this.dataSource.getRepository(ProjectDescriptorCategoryVariant)
    //             .findOneBy({id: id});
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Unable to refresh category variant',
    //             detail: 'Unable to pull category variant from server. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }
    //
    // async addEmptyVariant(category: ProjectDescriptorCategory): Promise<ProjectDescriptorCategoryVariant> {
    //     let result;
    //     try {
    //         const createResult = await this.dataSource.getRepository(ProjectDescriptorCategoryVariant)
    //             .insert({
    //                 enUsLabel: 'New Label',
    //                 enUsHelp: '',
    //                 uses: [],
    //                 questionType: 'TEXT',
    //                 categoryId: category.id
    //             });
    //         return await this.pullVariant(createResult.identifiers[0].id);
    //     } catch (error) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Unable to refresh category',
    //             detail: 'Unable to pull category from server. Please check log for details.',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return null;
    //     }
    // }

    getScreenName(): string {
        return this.dataStore.user.screenName;
    }

    debugPlatformUser(): PlatformUser {
        return this.dataStore.user;
    }

    getUserEmail(): string {
        return this.dataStore.user.email;
    }

    async fetchUserEmail() {
        const attributes = await fetchUserAttributes();

        return attributes.email;
    }

    getUserBio() {
        return this.dataStore.user.bioText;
    }


    async saveUserBio(user: PlatformUser) {
        try {
            await this.dataSource.getRepository(PlatformUser).update({uuid: user.uuid}, {bioText: user.bioText})
            console.log(this.dataStore.user);
            return "DONE";
        } catch (e) {
            console.log("error, failed to update bioText");
            return "FAILED";
        }
       
        
    }

    //private async updateAPILogin() {
    //const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    /* console.log('AUTHTOKEN: ', authToken);
            Amplify.configure(awsconfig, {
                API: {
                    REST: {
                        headers: async () => {
                            return {Authorization: authToken};
                        }
                    },
                }
            });
            const existingConfig = Amplify.getConfig();
            Amplify.configure({
                ...existingConfig,
                API: {
                    ...existingConfig.API,
                    REST: {
                        ...existingConfig.API?.REST,
                        'EtherpadAPI': {
                            endpoint: "https://la361oi2dk.execute-api.us-east-1.amazonaws.com",
                            region: 'us-east-1'
                        }
                    }
                },
            });*/
    //}

    private randomPassword(): string {
            let inStr =  (Math.random().toString(36).slice(2) +
            Math.random().toString(36)
                .toUpperCase().slice(2) + "!@#$%^&*" + Math.random().toString(36).slice(2) +
                Math.random().toString(36)
                    .toUpperCase().slice(2))+"!@#$%^&*";

            let shuffle = [...inStr];
            const getRandomValue = (i, N) => Math.floor(Math.random() * (N - i) + i);

            // Shuffle a pair of two elements at random position j (Fisher-Yates)
            shuffle.forEach( (elem, i, arr, j = getRandomValue(i, arr.length)) => [arr[i], arr[j]] = [arr[j], arr[i]] );
            return shuffle.join('').substring(0,26);
    }

    async getOrCreatePlatformUser() {
       

        const start = Date.now();
        let cognitoId: string;
        
        if(this.fetchLock == false) {
        this.progressService.blockWithMessage('Loading Profiles');
        this.fetchLock = true;
        if (!this.dataSource) {
            this.dataSource = await ORM.getORMDatasource();
        }
        try {
            this.progressService.blockWithMessage('Loading Profiles');
            //console.log(await fetchAuthSession());

            try {
                cognitoId = await this.getUserId();
            } catch (e) {
                console.log("Error retrieving cognitoId - Not logged in?")
                this.loggedIn.set(false);
                this.started.set(true);
                await this.router.navigateByUrl("/auth/login");
                return;
            }
            this.loggedIn.set(true);

            //Load from db and create base if needed
            // Old Query:
            // select pu.*, (SELECT row_to_json(t.*) FROM (SELECT brp.*, (SELECT json_agg(x.*) FROM (SELECT br_prj.*, (SELECT row_to_json(y.*) FROM (SELECT p.*, (SELECT json_agg(z.*)
            // FROM (SELECT pds.* FROM project_profile_selection pds WHERE pds.project_id=p.id) z) as project_profile_selections FROM project p WHERE p.id=br_prj.project_id) y) as project FROM beta_r
            // eader_project br_prj WHERE br_prj.beta_reader_profile_id = brp.id) x) as beta_reader_projects, (SELECT json_agg(z.*) FROM (SELECT brps.* FROM beta_reader_profile_selection brps WHERE b
            // rps.beta_reader_profile_id = brp.id) z) as beta_reader_profile_selections FROM beta_reader_profile brp WHERE brp.platform_user_id = pu.id) t) as beta_reader_profile, (SELECT row_to_jso
            // n(s.*) FROM (SELECT wp.*, (SELECT json_agg(b.*) FROM (SELECT p.*, (SELECT json_agg(a.*) FROM (SELECT pps.* FROM project_profile_selection pps WHERE pps.project_id = p.id) a) as project
            // _profile_selections FROM project p WHERE p.writer_profile_id=wp.id) b) as projects FROM writer_profile wp WHERE wp.platform_user_id=pu.id) s) as writer_profile FROM platform_user pu WH
            // ERE pu.cognito_acct=:cognitoId;
            const result: PlatformUser = await this.dataSource.getRepository(PlatformUser)
                .createQueryBuilder("pu")
                .leftJoinAndSelect("pu.awardedBadges", "badges")
                //.leftJoinAndSelect("pu.betaReaderProfile","brp")
                //.leftJoinAndSelect("brp.betaReaderProjects", "brps")
                //.leftJoinAndSelect("brps.project", "brpsp")
                //.leftJoinAndSelect("brpsp.writerProfile", "brpspw")
                //.leftJoinAndSelect("brpspw.platformUser", "brpspwp")
                //.leftJoinAndSelect("brp.profileGroups", "brppg")
                //.leftJoinAndSelect("brppg.profileSelections", "brppgps")
                //.leftJoinAndSelect("pu.writerProfile","wp")
                //.leftJoinAndSelect("wp.projects", "wps")
                //.leftJoinAndSelect("wps.profileGroup", "wpspg")
                //.leftJoinAndSelect("wpspg.profileSelections", "wpspgps")
                //.leftJoinAndSelect("wps.writerProfile", "wpsw")
                //.leftJoinAndSelect("wpsw.platformUser", "wpswp")
                //.leftJoinAndSelect("wps.projectProfileSelections", "wpsps")
                .where("pu.cognito_acct = :cognitoId", {cognitoId: cognitoId})
                .cache(JSON.stringify({
                    scope: 'LOCAL',
                    objectVersion: 4, //This needs changed when the query is changed or the objects returned are changed
                    name: this.STORAGE_KEY_PLATFORM_USER,
                    timeToLiveSeconds: 8*60*60, //8 hours before it is refreshed TODO: ensure this is updated on profile save
                    cacheId: cognitoId
                }))
                .getOne();

                console.log("HERE IS THE BASE QUERY RESULT", result);

            if (result && result.roles != null && result.roles.includes(PlatformRole.BETAREADER.valueOf())) {
                console.log("QUERYING BETAREADER PROFILE");
                const brResult = await this.dataSource.getRepository(BetaReaderProfile)
                    .createQueryBuilder("brp")
                    .leftJoinAndSelect("brp.betaReaderProjects", "brps")
                    .leftJoinAndSelect("brps.project", "brpsp")
                    .leftJoinAndSelect("brpsp.writerProfile", "brpspw")
                    .leftJoinAndSelect("brpspw.platformUser", "brpspwp")
                    .leftJoinAndSelect("brp.profileGroups", "brppg")
                    .leftJoinAndSelect("brppg.profileSelections", "brppgps")
                    .where("brp.platform_user_id = :puId", {puId: result.id})
                    .cache(JSON.stringify({
                    scope: 'LOCAL',
                    objectVersion: 1, //This needs changed when the query is changed or the objects returned are changed
                    name: this.STORAGE_KEY_PU_BETA_READER,
                    timeToLiveSeconds: 8*60*60, //8 hours before it is refreshed TODO: ensure this is updated on profile save
                    cacheId: cognitoId
                    }))
                    .getOne();

                result.betaReaderProfile = brResult;
            }


            if (result && result.roles != null && result.roles.includes(PlatformRole.WRITER.valueOf())) {
                console.log("QUERYING WRITER PROFILE");
                const wrResult = await this.dataSource.getRepository(WriterProfile)
                    .createQueryBuilder("wp")
                    .leftJoinAndSelect("wp.projects", "wps")
                    .leftJoinAndSelect("wps.profileGroup", "wpspg")
                    .leftJoinAndSelect("wpspg.profileSelections", "wpspgps")
                    .leftJoinAndSelect("wps.writerProfile", "wpsw")
                    .leftJoinAndSelect("wpsw.platformUser", "wpswp")
                    .where("wp.platform_user_id = :puId", {puId: result.id})
                    .cache(JSON.stringify({
                    scope: 'LOCAL',
                    objectVersion: 1, //This needs changed when the query is changed or the objects returned are changed
                    name: this.STORAGE_KEY_PU_WRITER,
                    timeToLiveSeconds: 8*60*60, //8 hours before it is refreshed TODO: ensure this is updated on profile save
                    cacheId: cognitoId
                    }))
                    .getOne();

                result.writerProfile = wrResult;
            }
            console.log("FINAL QUERY RESULT", result);
            // console.log("PlatformUser: ", resultArray);

            const attributes = (await fetchUserAttributes());

            if (!result) {
                //Doesn't exist - let's build it
                try {
                    const createResult = await this.dataSource.getRepository(PlatformUser)
                        .insert({
                                cognitoAcct: cognitoId,
                                email: attributes.email,
                                familyName: attributes.family_name,
                                givenName: attributes.given_name,
                                screenName: attributes.nickname,
                                uuid: uuid()
                            }
                        );
                    //Created - now run the standard process. . . .
                    await this.getOrCreatePlatformUser();
                    return;
                } catch (e) {
                    this.errorService.handleError({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Unable to create platform user record. See log for details',
                        error: e,
                        stack: e.stack
                    });
                    return;
                }
            }

            this.dataStore.user = result;

            if (this.dataStore.user.roles?.includes(PlatformRole.BETAREADER.valueOf())
                && !result.betaReaderProfile) {
                //Add it
                await this.addBetaReaderRole();
                return this.getOrCreatePlatformUser();
            }

            if (result.betaReaderProfile) {
                this.dataStore.betaReader = result.betaReaderProfile;
                console.log("HERE IS THE STORED USER IMMEDIATELY: ", this.dataStore);

                if (!this.dataStore.betaReader.profileGroups) {
                    this.dataStore.betaReader.profileGroups = [];
                }

                this.dataStore.betaReaderProjects = [];
                for (let brp of result.betaReaderProfile.betaReaderProjects) {
                    console.log(`Processing project ${brp.project.title} for beta reader`);
                    console.log("Profile Group: "+brp.project.profileGroup);
                    if (!brp.project.profileGroup) {
                        brp.project.profileGroup = new ProjectProfileGroup();
                        brp.project.profileGroup.profileSelections = [];
                        brp.project.profileGroup.projectSchemaCode = brp.project.schemaCode;
                    }
                    this.dataStore.betaReaderProjects.push({betaReaderProject: brp, project: brp.project});
                    console.log("Profile Group After: "+brp.project.profileGroup);
                }

                // for (const p of this.dataStore.betaReaderProjects) {
                //     const brps: ProjectProfileSelection[] = await this.dataSource.getRepository(ProjectProfileSelection)
                //         .createQueryBuilder("brps")
                //         .where("brps.projectId = :pId", {pId: p.project.id})
                //         .getMany();
                //     p.project.profileGroup = brps;
                //     this.fillOriginalValues(p.project.uuid, p.project.projectProfileSelections);
                // }

                this._betaReader.next(this.dataStore.betaReader);
                this._betaReaderProjects.next(this.dataStore.betaReaderProjects);

                //TODO - probably move this to a full "MatchProfiles" function that does it for all schemas
                await this.matchProjectToReader(this.dataStore.betaReader.getProfileGroupForCode("FICTION_SERIES"));

            }

            if (this.dataStore.user.roles?.includes(PlatformRole.WRITER.valueOf())
                && !result.writerProfile) {
                //Add it
                await this.addWriterRole();
                return this.getOrCreatePlatformUser();
            }

            if (result.writerProfile) {
                this.dataStore.writer = result.writerProfile;

                this.dataStore.writerProjects = result.writerProfile.projects;

                for (const p of this.dataStore.writerProjects) {
                    console.log(`Processing project ${p.title} for writer`);
                    console.log("Profile Group: "+p.profileGroup);
                    if (!p.profileGroup) {
                        p.profileGroup = new ProjectProfileGroup();
                        p.profileGroup.profileSelections = [];
                        p.profileGroup.projectSchemaCode = p.schemaCode;
                    }
                    console.log("Profile Group After: "+p.profileGroup);
                }

                // for (const p of this.dataStore.writer.projects) {
                //     const brps: ProjectProfileSelection[] = await this.dataSource.getRepository(ProjectProfileSelection)
                //         .createQueryBuilder("brps")
                //         .where("brps.projectId = :pId", {pId: p.id})
                //         .getMany();
                //     p.projectProfileSelections = brps;
                //     this.fillOriginalValues(p.uuid, p.projectProfileSelections);
                // }

                console.log("Notifying on Projects: ", this.dataStore.writerProjects);
                this._writer.next(this.dataStore.writer);
                this._writerProjects.next(this.dataStore.writerProjects);

                await this.getProjectMatchedReaders();
            }

            //Cognito roles are separate
            this.secureRoles = [];
            const payload = (await fetchAuthSession()).tokens.accessToken.payload['cognito:groups'];
            if (payload) {
                payload.toString().split(',').forEach(r => {
                    this.secureRoles.push(r);
                });
            }

            //No screenname? fix it . . .
            if (!attributes.nickname) {
                attributes.nickname = attributes.given_name;
            }
            //Check if there are changes - and store them in the DB
            if (this.dataStore.user.email != attributes.email ||
                this.dataStore.user.familyName != attributes.family_name ||
                this.dataStore.user.givenName != attributes.given_name ||
                this.dataStore.user.screenName != attributes.nickname ||
                this.dataStore.user.timezone != attributes.zoneinfo) {
                // console.log("Difference found");
                try {
                    await this.dataSource.getRepository(PlatformUser).update({id: this.dataStore.user.id}, {
                        email: attributes.email,
                        familyName: attributes.family_name,
                        givenName: attributes.given_name,
                        screenName: attributes.nickname,
                        timezone: attributes.zoneinfo
                    });

                    // console.log(updateResult);
                    await this.getOrCreatePlatformUser(); //Refresh after creation
                    return;
                } catch (e) {
                    this.errorService.handleError({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Unable to update platform user record after Cognito change. See log for details',
                        error: e,
                        stack: e.stack
                    });
                    return;
                }
            } else {
                console.log("No difference found - no need to update");
            }

            this.user.set(this.dataStore.user)
            DataService.s_user = this.dataStore.user;
            // console.log('Loaded from DB in ', Date.now() - start);
            //this.dehydrate();
            this.started.set(true);
            // console.log(this.dataStore.user);
            
        } finally {
            this.fetchLock = false;
            this.progressService.unBlock();
        }
        }
    }
    //get method ends here


    public async getHeaders(headers: HttpHeaders): Promise<HttpHeaders> {
        if (headers == null) {
            headers = new HttpHeaders();
        }
        let jwtToken = await this.getJWTToken();
        headers = headers.set('Authorization', jwtToken);
        return headers;
    }

    async getJWTToken(): Promise<string> {
        return (await fetchAuthSession()).tokens.idToken.toString();
    }

    async getUserId(): Promise<string> {
        var authenticatedUser = await getCurrentUser();
        return authenticatedUser.userId.toString();
    }

    async addWriterRole() {
        try {
            if (!this.dataStore.writer) {

                const wpResult = await this.dataSource.getRepository(WriterProfile)
                    .insert({
                        platformUserId: this.dataStore.user.id
                    });

                /*const wpResult = await this.dataSource.getRepository(PlatformUser); {
                    query: mutations.createWriterProfileAutoID,
                    variables: {
                        platformUserId: this.dataStore.user.id
                    }
                });*/
            }
            await this.addAppRole(PlatformRole.WRITER.valueOf());
        } catch (e) {
            this.errorService.handleError({
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update platform user with Writer role. See log for details',
                error: e,
                stack: e.stack
            });
        }
    }

    async addBetaReaderRole() {
        try {
            if (!this.dataStore.betaReader) {
                const wpResult = await this.dataSource.getRepository(BetaReaderProfile)
                    .insert({
                        platformUserId: this.dataStore.user.id
                    });
            }
            await this.addAppRole(PlatformRole.BETAREADER.valueOf());
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update platform user with Beta Reader role. See log for details'
            });
        }
    }

    async getUserCount() {
       //queries database for general PlatformUser count
        let userCountVariable = await this.dataSource.getRepository(PlatformUser).createQueryBuilder("user").where("user.id IS NOT NULL").getCount();

        return userCountVariable;
    }

    async getProjectCount() {
        //queries database for general Project count
        let projectCountVariable = await this.dataSource.getRepository(Project).createQueryBuilder("project").where("project.hasContent IS NOT NULL").getCount();

        return projectCountVariable;
    }

    async checkForBadge(BadgeCode: string) {
        const user = await this.dataSource.getRepository(AwardedBadges).createQueryBuilder("badge").where("badge.badge_code = :badgeCode AND badge.platform_user_id = :userId AND badge.earn_date IS NOT NULL", 
                    {badgeCode: BadgeCode, userId: this.user().id})
                    .getCount();

        if (user == 0) {
            return "NULL";
        } else {
            return "EARNED";
        }
    }

    async earnBadge(BadgeCode: string) {
        const user = await this.dataSource.getRepository(AwardedBadges).createQueryBuilder("badge").where("badge.badge_code = :badgeCode AND badge.platform_user_id = :userId AND badge.earn_date IS NOT NULL", 
                    {badgeCode: BadgeCode, userId: this.user().id})
                    .getCount();
        console.log("this is the earnBadge getCount value:" + user);
        if (user != 0) {
            console.log("we are not inserting, row exists");
            return;
        } else if (user == 0) {
            console.log("we are inserting, row doesn't exist");
            await this.dataSource.getRepository(AwardedBadges).insert({
            platformUser: this.user(), 
            badgeCode: BadgeCode, 
            earnDate: new Date()
            });
        }
    }


    private async addAppRole(role: string) {
        if (!this.dataStore.user.roles) {
            this.dataStore.user.roles = [];
        }
        if (this.dataStore.user.roles.includes(role)) {
            return; //Nothing to do
        }
        this.dataStore.user.roles.push(role);

        try {
            await this.dataSource.getRepository(PlatformUser).update({id: this.dataStore.user.id}, {
                roles: this.dataStore.user.roles
            });

            // console.log(updateResult);
            await this.getOrCreatePlatformUser(); //Refresh after creation
            return;
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update platform user record after role update. See log for details'
            });
            return;
        }
    }

    redirectToHome(force: boolean = false) {
        console.log('REDIRECT_URL', this.redirectUrl, this.dataStore.user);
        if (this.redirectUrl && this.redirectUrl !== '/' && !this.redirectUrl.startsWith('/auth') && !this.redirectUrl.startsWith('/startup')) {
            const url = this.redirectUrl;
            this.redirectUrl = null;
            this.router.navigateByUrl(url);
            return;
        }
        // console.log('REDIRECTED FROM SOMEWHERE');
        // console.trace();
        // if (!force && this.isRnGAdmin()) {
        //    return; //If they are admins, assume they know what they are doing. . .
        // }
        if (!this.loggedIn) {
            console.log('Navigating to login');
            this.router.navigate(['/auth/login']);
        // } else if (this.isRnGAdmin()) {
        //     console.log('Navigating to Admin');
        //     this.router.navigate(['/', 'admin']);
        } else if (!this.dataStore.user?.roles || this.dataStore.user.roles.length === 0) {
            console.log('Navigating to startup');
            this.router.navigate(['/', 'startup']);
        } else if (this.dataStore.user.roles.includes(PlatformRole.WRITER.valueOf())) {
            console.log('Navigating to Writer');
            this.router.navigate(['/', 'writer']);
        } else if (this.dataStore.user.roles.includes(PlatformRole.BETAREADER.valueOf())) {
            console.log('Navigating to BetaReader');
            this.router.navigate(['/', 'beta_reader','new']);
        } else {
            console.log('Navigating to login (fallthrough)');
            this.router.navigate(['/auth/login']);
        }

    }

    async signOut() {
        try {
            console.log("Signing out");
            await signOut();
            this.loggedIn.set(false);
            await this.getOrCreatePlatformUser();
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to sign out. See log for details'
            });
        }
    }

    async requestResetPassword(username: string): Promise<string> {
        try {
            const {nextStep} = await resetPassword({username});
            return nextStep.resetPasswordStep.toString();

        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to request password reset. See log for details'
            });
            return "ERROR";
        }
    }

    async confirmResetPassword({
                                   username,
                                   confirmationCode,
                                   newPassword
                               }: ConfirmResetPasswordInput): Promise<string> {
        try {
            await confirmResetPassword({username, confirmationCode, newPassword});
            return "DONE";
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to confirm password reset. See log for details'
            });
            return "ERROR";
        }
    }

    async handleUpdatePassword({
        oldPassword,
        newPassword,
    }: UpdatePasswordInput) {
        try{
            await updatePassword({oldPassword, newPassword})
            return "DONE";
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to confirm password change. See log for details'
            });
            return "ERROR";
        }

    }

    async handleUpdateEmail (updatedEmail: string) {
        try {
            const attributes = await updateUserAttributes({
                userAttributes: {
                    email: updatedEmail,
                }
            })
            return "DONE";
        } catch (e) {
             this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to send confirmation code. See log for details'
            });
            return "ERROR";
        }
    }

    async handleConfirmUpdateEmail ({userAttributeKey, confirmationCode}: ConfirmUserAttributeInput) {
            try {
                await confirmUserAttribute({userAttributeKey, confirmationCode});
                return "DONE";
            } catch (e) {
                this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to confirm email change. See log for details'
            });
            return "ERROR";
            }
    }


    async signUp({
                     username, password, email,
                     given_name, family_name,
                     nickname
                 }): Promise<string> {
        try {
            let body = {
                username: username,
                password: password,
                options: {
                    userAttributes: {
                        email: email,
                        given_name: given_name,
                        family_name: family_name
                    },
                    autoSignIn: false
                }
            };
            if (nickname) {
                body.options.userAttributes['nickname'] = nickname;
            }
            const {isSignUpComplete, userId, nextStep} = await signUp(body);
            switch (nextStep.signUpStep) {
                case 'DONE':
                    //Success - no additional needs
                    await this.getOrCreatePlatformUser();
                    return "DONE";
                case 'CONFIRM_SIGN_UP':
                    //Success - go to confirmation screen
                    return "CONFIRM_SIGN_UP";
                default:
                    return nextStep.signUpStep;
            }
        } catch (e) {
            console.log('LOGIN ERROR:',e);
            if (e.name === "UsernameExistsException") {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Sign-up Failed',
                    detail: 'There is already an account for this email address. Please use the "Forget Password" link, below.'
                });
            } else {
                this.errorService.handleError({
                    error: e,
                    stack: e.stack,
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Unable to sign up. See log for details'
                });
            }
            return "ERROR";
        }
    }

    async confirmSignUp({
                            username,
                            confirmationCode
                        }: ConfirmSignUpInput): Promise<string> {
        try {
            const {isSignUpComplete, nextStep} = await confirmSignUp({
                username,
                confirmationCode
            });
            switch (nextStep.signUpStep) {
                case 'DONE':
                    //Success
                    await this.getOrCreatePlatformUser();
                    return "DONE";
                default:
                    return nextStep.signUpStep;
            }
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to confirm sign up. See log for details'
            });
            return "ERROR";
        }
    }

    async signIn({username, password}: SignInInput): Promise<string> {
        try {
            const {isSignedIn, nextStep} = await signIn({username, password});
            switch (nextStep.signInStep) {
                case 'CONFIRM_SIGN_UP':
                    //Need to confirm
                    console.log("Need to confirm");
                    await this.getOrCreatePlatformUser();
                    return "CONFIRM_SIGN_UP";
                case 'DONE':
                    //Success
                    console.log("success - logged in");
                    //GET REPOSITORY and objects SET UP HERE
                    await this.getOrCreatePlatformUser();
                    console.log("User and supporting objects built");
                    return "DONE";
                default:
                    return nextStep.signInStep;
            }
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to sign in. Please check your email address and password.'
            });
            console.log(e);
            return "ERROR";
        }
    }

    isRnGAdmin(): boolean {
        return this.secureRoles.includes(SecureRole.SUPERADMIN.valueOf());
    }

    isDeveloper() {
        return this.secureRoles.includes(SecureRole.DEVELOPER.valueOf());
    }

    async checkScreenName(screenName: string): Promise<{ screenNameNotAllowed: boolean, screenNameError: string }> {
        console.log("Check Screen Name");
        let screenNameError: string;
        let screenNameNotAllowed: boolean;
        if (screenName.length > 24) {
            screenNameError = `The name ${screenName} is too long. Please try another.`;
            screenNameNotAllowed = true;
            return {screenNameNotAllowed: screenNameNotAllowed, screenNameError: screenNameError};
        }
        if (this.badWords.isProfane(screenName)) {
            screenNameError = "You can't use this screen name. Please try another.";
            screenNameNotAllowed = true;
            return {screenNameNotAllowed: screenNameNotAllowed, screenNameError: screenNameError};
        }

        if (!this.dataSource) {
            this.dataSource = await ORM.getORMDatasource();
        }

        console.log(this.dataSource);
        const isInUse = await this.dataSource.getRepository(PlatformUser).exists({
            where: {
                screenName: screenName
            }
        });
        if (isInUse) {
            screenNameError = `The name ${screenName} is already taken. Please try another.`;
            screenNameNotAllowed = true;
            return {screenNameNotAllowed: screenNameNotAllowed, screenNameError: screenNameError};
        }
        screenNameNotAllowed = false;
        return {screenNameNotAllowed: screenNameNotAllowed, screenNameError: screenNameError};
    }

    async saveBetaReader(betareader: BetaReaderProfile) {
        console.log(betareader);
        try {
            if (!betareader.id) {
                const result = await this.dataSource.getRepository(BetaReaderProfile).insert(betareader);
                betareader.id = Number.parseInt(result.raw.rows[0].id, 10);
                console.log("Saving BetaReader", betareader, result);
            } else {
                const repo = await this.dataSource.getRepository(BetaReaderProfile);
                const p = await repo.findOneBy({id: betareader.id});
                console.log(p);
                Object.assign(p, betareader);
                await repo.save(p);
            }
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update betareader core. See log for details'
            });
        }
    }

    async saveReaderProfileGroup(betaReader: BetaReaderProfile, profileGroup: ProjectProfileGroup) {
        this.progressService.blockWithMessage("Saving Choices");
        try {
            if(!betaReader.id) {
                //this.progressService.unBlock;
                return null;
            }else if (!profileGroup.id){
                console.log("NO PROFILE GROUP ID, INSERTING TO DB")
                const result = await this.dataSource.getRepository(ProjectProfileGroup).insert(profileGroup);
                profileGroup.id = Number.parseInt(result.raw.rows[0].id, 10);
                betaReader.profileGroups.push(profileGroup);
                const brRepo = await this.dataSource.getRepository(BetaReaderProfile);
                const brResult = await brRepo.findOneBy({id: betaReader.id});
                Object.assign(brResult, betaReader);
                await brRepo.save(brResult);
            } else {
                const ppgRepo = await this.dataSource.getRepository(ProjectProfileGroup);
                const ppgResult = await ppgRepo.findOneBy({id: profileGroup.id});
                console.log("PROFILE GROUP RESULT", ppgResult);
                Object.assign(ppgResult, profileGroup);
                await ppgRepo.save(ppgResult);
            }
            for (const selection of profileGroup.profileSelections) {
                if (!selection.id) {
                    console.log("SELECTION ID DIDN'T EXIST");
                    selection.profileGroupId = profileGroup.id;
                    const selectionResult = await this.dataSource.getRepository(ProjectProfileSelection).insert(selection);
                    console.log(selectionResult);
                    selection.id = Number.parseInt(selectionResult.raw.rows[0].id, 10);
                } else {
                    console.log("SELECTION ID DID EXIST???");
                    
                    await this.dataSource.createQueryBuilder()
                    .update(ProjectProfileSelection).set({value: selection.value}).where("id = :id AND profile_group_id = :pgid", {id: selection.id, pgid: profileGroup.id}).execute();
                    //Currently leaving the following query code for review, I changed it from repo.save to a strictly update query because save was both inneficient and was occasionally duplicating table rows.
                    /*const sRepo = await this.dataSource.getRepository(ProjectProfileSelection);
                    const s = await sRepo.findOneBy({id: selection.id, profileGroupId: profileGroup.id});
                    Object.assign(s, selection);
                    await sRepo.save(s);*/
                }
            }
            console.log("I AM DONE UPDATING THE SELECTIONS");
            
            
        } catch (e) {
                this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update profile selection. See log for details'
            });
        } finally {
            this.progressService.unBlock();
        }
        
    }

    async saveCoreProject(project: Project) {
        console.log(project);
        try {
            if (!project.id) {
                const result = await this.dataSource.getRepository(Project).insert(project);
                project.id = Number.parseInt(result.raw.rows[0].id, 10);
                console.log("Saving project", project, result);
            } else {
                const repo = this.dataSource.getRepository(Project);
                const p = await repo.findOneBy({id: project.id});
                Object.assign(p, project);
                await repo.save(p);
            }
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update project core. See log for details'
            });
        }
    }

    async saveProjectProfileSelections(project: Project, profileGroup: ProjectProfileGroup) {
        try {
            if (!profileGroup.id) {
                console.log("NO PROFILE GROUP ID, INSERTING TO DB")
                const result = await this.dataSource.getRepository(ProjectProfileGroup).insert(profileGroup);
                profileGroup.id = Number.parseInt(result.raw.rows[0].id, 10);
                project.profileGroup = profileGroup;
                const pRepo = await this.dataSource.getRepository(Project);
                const pResult = await pRepo.findOneBy({id: project.id});
                Object.assign(pResult, project);
                await pRepo.save(pResult);
            } else {
                const ppgRepo = await this.dataSource.getRepository(ProjectProfileGroup);
                const ppgResult = await ppgRepo.findOneBy({id: profileGroup.id});
                console.log("PROFILE GROUP RESULT", ppgResult);
                Object.assign(ppgResult, profileGroup);
                await ppgRepo.save(ppgResult);
            }
            /*project.profileGroup = profileGroup;
            const pRepo = await this.dataSource.getRepository(Project);
            const pResult = await pRepo.findOneBy({id: project.id});
            Object.assign(pResult, project);
            await pRepo.save(pResult);*/
            for (const selection of profileGroup.profileSelections) {
                if (!selection.id) {
                    console.log("SELECTION ID DIDN'T EXIST");
                    selection.profileGroupId = profileGroup.id;
                    const selectionResult = await this.dataSource.getRepository(ProjectProfileSelection).insert(selection);
                    console.log(selectionResult);
                    selection.id = Number.parseInt(selectionResult.raw.rows[0].id, 10);
                } else {
                    console.log("SELECTION ID DID EXIST???");
                    
                    await this.dataSource.createQueryBuilder()
                    .update(ProjectProfileSelection).set({value: selection.value}).where("id = :id AND profile_group_id = :pgid", {id: selection.id, pgid: profileGroup.id}).execute();
                }
            }
            console.log("I AM DONE UPDATING THE WRITERS SELECTIONS");
        } catch (e) {
             this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update project selections. See log for details'
            });
        } finally {

        }
    }

    async saveProjectTempToProfile(project: Project, profileGroup: ProjectProfileGroup) {
        this.progressService.blockWithMessage('Saving Project');
        try {
            await this.saveCoreProject(project);
            await this.saveProjectProfileSelections(project, profileGroup);
            // let updates: { id: number, projectDescriptorId: number, projectId: number, value: string }[] = [];
            // let creates: { projectId: number, projectDescriptorId: number, value: string }[] = [];
            // const values = this.getPDCurrentValues(namespace);
            // for (const keyS of Object.keys(values)) {
            //     const key: number = Number.parseInt(keyS, 10);
            //     let found = false;
            //     for (const pde of project.projectProfileSelections) {
            //         if (pde.projectDescriptorId === key) {
            //             found = true;
            //             updates.push({
            //                 id: pde.id,
            //                 projectId: project.id,
            //                 projectDescriptorId: key,
            //                 value: values[key]
            //             });
            //             break;
            //         }
            //     }
            //     if (!found) {
            //         creates.push({
            //             value: values[key],
            //             projectId: project.id,
            //             projectDescriptorId: key
            //         });
            //         console.log("Creating pps: ", project.id, project, creates)
            //     }
            // }
            // for (const u of updates) {
            //     await this.dataSource.getRepository(ProjectProfileSelection).update(u.id, u);
            // }
            // //for (const c of creates) {
            // await this.dataSource.getRepository(ProjectProfileSelection).insert(creates);
            // //}
            this.progressService.unBlock();
        } catch (e) {
            this.progressService.unBlock();
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update project profile. See log for details'
            });
        }
    }

    newProject(wp: WriterProfile): Project {
        const project: Project = {
            usesPenName: false,
            schemaCode: ProjectSchemas.DEFAULT_PROJECT_SCHEMA_DISPLAY.code,
            lookingForBetaReaders: false,
            uuid: uuid(),
            hasContent: false,
            title: null,
            summary: null,
            lastInfo: null,
            id: null,
            profileGroup: new ProjectProfileGroup(),
            writerProfile: wp,
            penNameGivenName: null,
            penNameSurname: null,
            betaReaderProjects: []
        };
        project.profileGroup.profileSelections = [];
        project.profileGroup.projectSchemaCode = project.schemaCode;

        // const projectProfileSelections: ProjectProfileSelection[] = [];
        // for (const pdc of projectDescriptorCategories) {
        //     for (const pd of pdc.descriptors) {
        //         projectProfileSelections.push({
        //             project: project,
        //             projectDescriptorId: pd.id,
        //             projectId: null,
        //             id: null,
        //             value: null
        //         });
        //     }
        // }
        // project.projectProfileSelections = projectProfileSelections;
        return project;
    }


    // stopMatches() {
    //     if (this.betaReaderSubscription) {
    //         this.betaReaderSubscription.unsubscribe();
    //     }
    // }
    //
    // async updateCategoriesInProjectMatch(prjMatch: ProjectMatchType) {
    //     const categories: DisplayCategoryType[] = [];
    //     //console.log('CATEGORIES: ', this.betaReaderViewOfProjectDescriptorCategories);
    //     for (const category of projectDescriptorCategories) {
    //         const variants = category.variants.filter(pdcv => pdcv.uses.includes(ProjectDescriptorUse.BETA_READER_PROFILE));
    //
    //         if (variants.length === 0) {
    //             continue;
    //             /*this.errorService.handleError({
    //                 error: new Error('Category has no matching variant'),
    //                 stack: null,
    //                 ancillaries: [category],
    //                 severity: 'info',
    //                 summary: 'Configuration Issue',
    //                 detail: 'Category has no matching variant. See log for details',
    //             });*/
    //         }
    //         const variant = variants[0];
    //         //console.log('Variant: ', variant);
    //         //console.log('PrjMatch ', prjMatch);
    //         switch (variant.questionType) {
    //             case ProjectDescriptorType.CHECKBOXES.valueOf():
    //             case ProjectDescriptorType.RADIO_BUTTONS.valueOf():
    //             case ProjectDescriptorType.DROPDOWN.valueOf():
    //             case ProjectDescriptorType.MULTISELECT.valueOf():
    //                 categories.push({
    //                     enUsLabel: variant.enUsLabel,
    //                     icon: variant.tagIcon,
    //                     color: variant.tagColor,
    //                     value: this.getSelectionArray(category, prjMatch.project.projectProfileSelections)
    //                 });
    //                 break;
    //             case ProjectDescriptorType.TEXT.valueOf():
    //             case ProjectDescriptorType.LONGTEXT.valueOf():
    //             case ProjectDescriptorType.NUMBER.valueOf():
    //                 const value: string = prjMatch.project.projectProfileSelections[0] ?
    //                     prjMatch.project.projectProfileSelections[0].value :
    //                     '';
    //                 categories.push({
    //                     enUsLabel: variant.enUsLabel,
    //                     icon: variant.tagIcon,
    //                     color: variant.tagColor,
    //                     value: value
    //                 });
    //                 break;
    //         }
    //     }
    //     prjMatch.categories = categories;
    //     console.log('Project Match: ', prjMatch);
    //     return prjMatch;
    // }
    //
    // async getBetaReaderProjectMatchTypes(betaReaderProjects: BetaReaderProject[], lookingForBetaReadersOnly: boolean) {
    //     console.log("BetaReaderProjects: ", betaReaderProjects);
    //     let ret: ProjectMatchType[] = [];
    //     let qb = this.dataSource.getRepository(Project)
    //         .createQueryBuilder("p")
    //         .where("false");
    //     const brpMap: Map<number, BetaReaderProject> = new Map<number, BetaReaderProject>();
    //     betaReaderProjects.forEach((brp, idx) => {
    //         if (lookingForBetaReadersOnly) {
    //             qb.orWhere(
    //                 new Brackets((qb) => {
    //                     qb.where("p.lookingForBetaReaders")
    //                         .andWhere("p.id = :pid_"+idx, {["pid_"+idx]: brp.project.id});
    //                 })
    //             );
    //         } else {
    //             qb.orWhere("p.id = :pid_"+idx, {["pid_"+idx]: brp.project.id});
    //         }
    //         brpMap.set(brp.project.id, brp);
    //
    //     });
    //     const projectResult = await qb.getMany();
    //
    //     for (const prj of projectResult) {
    //         const entry: ProjectMatchType = {
    //             author: prj.usesPenName ? prj.penNameGivenName + ' ' + prj.penNameSurname : '',
    //             betaReaderProject: brpMap.get(prj.id),
    //             categories: null,
    //             project: prj,
    //             score: 0,
    //             title: prj.title ? prj.title : ''
    //         };
    //         await this.updateCategoriesInProjectMatch(entry);
    //         ret.push(entry);
    //     }
    //     return ret;
    // }
    //
    // private betaReaderSubscription: Subscription;
    //
    // async getMatches(profileId: number): Promise<Record<string, ProjectMatchType>> {
    //     // console.log('Loading Matches');
    //     this.progressService.blockWithMessage('Loading Matches');
    //     try {
    //         const result = await this.dataSource.getRepository(BetaReaderProject)
    //             .createQueryBuilder("brp")
    //             .where({
    //                 betaReaderProfileId: profileId
    //             })
    //             .getMany();
    //         for (const prj of result) {
    //             if (!prj.betaReaderStatus) {
    //                 prj.betaReaderStatus = BetaReaderStatus.SEEKING_READERS.valueOf();
    //             }
    //         }
    //         // console.log('Narfle', result);
    //         if (result.length > 0) {
    //             const entries = await this.getBetaReaderProjectMatchTypes(result, true);
    //             this.dataStore.betaMatches = {};
    //             for (const entry of entries) {
    //                 this.dataStore.betaMatches[entry.betaReaderProject.uuid] = entry;
    //             }
    //         }
    //         // console.log('Narfle2', profileId);
    //         //"select * from project p
    //         const projects = await this.dataSource.getRepository(Project)
    //             .createQueryBuilder("p")
    //             // where
    //             //   (p.id not in
    //             //      (select project_id from beta_reader_project where beta_reader_profile_id = :betaReaderProfileId)
    //             //   )
    //             .where(qb => {
    //                 const subQuery = qb.subQuery()
    //                     .select("brp.project_id")
    //                     .from(BetaReaderProject, "brp")
    //                     .where("brp.beta_reader_profile_id = :betaReaderProfileId")
    //                     .getQuery();
    //                 return "p.id NOT IN " + subQuery;
    //             })
    //             //   and (p.id not in
    //             //   )
    //             .andWhere(qb => {
    //                 const subQuery = qb.subQuery()
    //                     //      (select project_id from project_profile_selection pps
    //                     .select("pps.project_id")
    //                     .from(ProjectProfileSelection, "pps")
    //                     //         left join project_descriptor pd on pd.id=pps.project_descriptor_id
    //                     .leftJoin(ProjectDescriptor, "pd", "pd.id=pps.project_descriptor_id")
    //                     //         left join project_descriptor_category pdc on pdc.id=pd.category_id
    //                     .leftJoin(ProjectDescriptorCategory, "pdc", "pdc.id=pd.category_id")
    //                     //         left join beta_reader_profile_selection brps
    //                     //            on pps.project_descriptor_id=brps.project_descriptor_id
    //                     //            and pps.value=brps.value
    //                     //            and brps.beta_reader_profile_id=:betaReaderProfileId
    //                     .leftJoin(BetaReaderProfileSelection, "brps",
    //                         "pps.project_descriptor_id=brps.project_descriptor_id "+
    //                         "and pps.value=brps.value "+
    //                         "and brps.beta_reader_profile_id=:betaReaderProfileId"
    //                     )
    //                     //       where pdc.beta_reader_match_use='MUST_NOT_MATCH_ANY'
    //                     .where("pdc.beta_reader_match_use='MUST_NOT_MATCH_ANY'")
    //                     //         and brps.id is not null
    //                     .andWhere("brps.id is not null")
    //                     //         and brps.value is not null
    //                     .andWhere("brps.vale is not null")
    //                     //         and brps.value = 'true'
    //                     .andWhere("brps.value = 'true'")
    //                     //      )
    //                     .getQuery();
    //                 return "p.id NOT IN " + subQuery;
    //             })
    //             //   and (p.id in
    //             .andWhere(qb => {
    //                 const subQuery = qb.subQuery()
    //                     //      (select project_id from project_profile_selection pps
    //                     .select("pps.project_id")
    //                     .from(ProjectProfileSelection, "pps")
    //                     //         left join project_descriptor pd on pd.id=pps.project_descriptor_id
    //                     .leftJoin(ProjectDescriptor, "pd", "pd.id=pps.project_descriptor_id")
    //                     //         left join project_descriptor_category pdc on pdc.id=pd.category_id
    //                     .leftJoin(ProjectDescriptorCategory, "pdc", "pdc.id=pd.category_id")
    //                     //         left join beta_reader_profile_selection brps
    //                     //            on pps.project_descriptor_id=brps.project_descriptor_id
    //                     //            and pps.value=brps.value
    //                     //            and brps.beta_reader_profile_id=:betaReaderProfileId
    //                     .leftJoin(BetaReaderProfileSelection, "brps",
    //                         "pps.project_descriptor_id=brps.project_descriptor_id "+
    //                         "and pps.value=brps.value "+
    //                         "and brps.beta_reader_profile_id=:betaReaderProfileId"
    //                     )
    //                     //       where pdc.beta_reader_match_use='MUST_MATCH_AT_LEAST_ONE'
    //                     .where("pdc.beta_reader_match_use='MUST_MATCH_AT_LEAST_ONE'")
    //                     //         and brps.id is not null
    //                     .andWhere("brps.id is not null")
    //                     //         and brps.value is not null
    //                     .andWhere("brps.vale is not null")
    //                     //         and brps.value = 'true'
    //                     .andWhere("brps.value = 'true'")
    //                     //      )
    //                     .getQuery();
    //                 return "p.id IN " + subQuery;
    //             })
    //             .andWhere("p.looking_for_beta_readers")
    //             .setParameter("betaReaderProfileId", profileId)
    //             .getMany();
    //         console.log('Result '+profileId+': ', projects);
    //         //pending-response, seeking-readers, active, not-interested
    //         if (projects.length > 0) {
    //             for (const prj of projects) {
    //                 const betaReaderProject: BetaReaderProject = {
    //                     betaReaderProfile: undefined, colorIndex: 0, writerStatus: "",
    //                     betaReaderProfileId: profileId,
    //                     betaReaderStatus: BetaReaderStatus.SEEKING_READERS.valueOf(),
    //                     project: prj,
    //                     projectId: prj.id,
    //                     uuid: uuid(),
    //                     id: null
    //                 };
    //
    //                 let score = 0;
    //                 //TODO: Fix score . . .
    //                 /*for (let p of projects) {
    //                     if (p.id === prj.id && p.score) {
    //                         score = p.score;
    //                     }
    //                 }*/
    //
    //                 let entry: ProjectMatchType = {
    //                     project: prj,
    //                     title: prj.title,
    //                     author: prj.usesPenName ? prj.penNameGivenName + ' ' + prj.penNameSurname : 'Author Name',
    //                     score: (score ? score : 0),
    //                     betaReaderProject: betaReaderProject,
    //                     categories: null
    //                 }
    //                 await this.updateCategoriesInProjectMatch(entry);
    //                 this.dataStore.betaMatches[betaReaderProject.uuid] = entry;
    //             }
    //         }
    //         this._betaMatches.next(this.dataStore.betaMatches);
    //         return this.dataStore.betaMatches;
    //     } catch (error) {
    //         // console.log('BETA_READER ERROR:', error);
    //         // console.trace();
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error',
    //             detail: 'Unable to load beta reader project record. See log for details',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return this.dataStore.betaMatches;
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }
    //
    // private getSelectionArray(category: ProjectDescriptorCategory, projectProfileSelections: ProjectProfileSelection[]): string[] {
    //     let ret: string[] = [];
    //     for (const pd of category.descriptors) {
    //         for (let pps of projectProfileSelections) {
    //             if (pps.projectDescriptorId === pd.id) {
    //                 if (pps.value === 'true') {
    //                     ret.push(pd.label);
    //                 }
    //                 break;
    //             }
    //         }
    //     }
    //     return ret;
    // }
    //
    // async startProjectResourceMatches(projectId: number) {
    //     console.log('Loading Writer-Project Matches', projectId);
    //     if (!projectId) {
    //         console.log('No projectId when going to load writer project matches');
    //         return;
    //     }
    //     this.progressService.blockWithMessage('Loading Available Resources');
    //     try {
    //         const result = await this.dataSource.getRepository(BetaReaderProject)
    //             .createQueryBuilder("brp")
    //             .leftJoinAndSelect("brp.betaReaderProfile", "brpr")
    //             .leftJoinAndSelect("brpr.platformUser", "pu")
    //             .where({projectId: projectId})
    //             .getMany();
    //         console.log('WRITER MATCH Result: ', result);
    //
    //         this.dataStore.projectResourceMatches = {};
    //         //pending-response, seeking-readers, active, not-interested
    //         for (const project of result) {
    //             this.dataStore.projectResourceMatches[project.uuid] = {
    //                 project: project,
    //                 name: (project.betaReaderProfile.platformUser.screenName)
    //                     ? project.betaReaderProfile.platformUser.screenName
    //                     : project.betaReaderProfile.platformUser.givenName + ' ' + project.betaReaderProfile.platformUser.familyName,
    //                 bioSketch: project.betaReaderProfile.bioSketch,
    //                 resourceType: WriterResourceType.BETA_READER
    //             };
    //         }
    //
    //         this._projectResourceMatches.next(this.dataStore.projectResourceMatches);
    //     } catch (error) {
    //         console.log(error);
    //         console.log("errorService: ", this.errorService);
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error',
    //             detail: 'Unable to load writer project record. See log for details',
    //             error: error,
    //             stack: error.stack
    //         });
    //         return;
    //     } finally {
    //         this.progressService.unBlock();
    //     }
    // }

    async setBetaReaderStatus(betaReaderProject: BetaReaderProject, newStatus: BetaReaderStatus) {
        try {
            const result = await this.dataSource.getRepository(BetaReaderProject).update({ uuid: betaReaderProject.uuid},
                {
                    betaReaderStatus: newStatus.valueOf()
                }
            );
            //this.startMatches(betaReaderProject.betaReaderProfileId);
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                ancillaries: [uuid, newStatus],
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to change Beta Reader Status for project. Check log for details.'
            });
        }
    }

    async setBRPWriterStatus(betaReaderProject: BetaReaderProject, newStatus: WriterStatus) {
        try {
            const result = await this.dataSource.getRepository(BetaReaderProject).update({uuid: betaReaderProject.uuid},
                {
                    writerStatus: newStatus.valueOf()
                }
            );
            //this.startMatches(betaReaderProject.betaReaderProfileId);
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                ancillaries: [uuid, newStatus],
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to change Writer Status for project. Check log for details.'
            });
        }
    }

    async saveBetaReaderProject(betaReaderProject: BetaReaderProject) {
        try {
            const result = await this.dataSource.getRepository(BetaReaderProject).insert(betaReaderProject);
            console.log(result);
            betaReaderProject.id = result.identifiers[0].id;
            return "SUCCESS";
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                ancillaries: [betaReaderProject],
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to change Beta Reader Status for project. Check log for details.'
            });
            return "FAIL";
        }
    }

    public getHydrationUUID(): string {
        return this.dataStore.hydrationUUID;
    }

    getRoleBasedProjectForUUID(uuid: string): RoleProject {
        //Add a clause to the return and to the below for different non-author types

        if (this.dataStore.betaReaderProjects) {
            for (const p of this.dataStore.betaReaderProjects) {
                if (p.betaReaderProject.uuid === uuid) {
                    return {
                        colorIndex: p.betaReaderProject.colorIndex,
                        project: p.project,
                        role: PlatformRole.BETAREADER,
                        roleProject: p.betaReaderProject
                    };
                }
            }
        }
        if (this.dataStore.writerProjects) {
            for (const p of this.dataStore.writerProjects) {
                if (p.uuid === uuid) {
                    return {
                        colorIndex: 0,
                        project: p,
                        role: PlatformRole.WRITER,
                        roleProject: null
                    };
                }
            }
        }
        return null;
    }

    getProjectForUUID(uuid: string): Project {
        //await console.log("WP: ", this.dataStore);

        if (this.dataStore.writerProjects) {
            for (const p of this.dataStore.writerProjects) {
                if (p.uuid === uuid) {
                    return p;
                }
            }
        }
        if (this.dataStore.betaReaderProjects) {
            for (const p of this.dataStore.betaReaderProjects) {
                if (p.betaReaderProject.uuid === uuid) {
                    return p.project;
                }
            }
        }
        return null;
    }

    // async saveProject(project: Project) {
    //     try {
    //         let upi = [{
    //             id: project.id,
    //             summary: project.summary,
    //             looking_for_beta_readers: project.lookingForBetaReaders,
    //             title: project.title,
    //             pen_name_given_name: project.penNameGivenName,
    //             pen_name_surname: project.penNameSurname,
    //             uses_pen_name: project.usesPenName,
    //             pad_group_id: project.padGroupId,
    //             pad_initialized: project.padInitialized,
    //             pad_main_rw_pad_id: project.padMainRwPadId,
    //             pad_main_ro_pad_id: project.padMainRoPadId,
    //         }];
    //
    //         await this.dataSource.getRepository(Project).update(project.id, project);
    //         // console.log(updateResult);
    //         await this.getOrCreatePlatformUser();
    //         return;
    //     } catch (e) {
    //         this.errorService.handleError({
    //             severity: 'error',
    //             summary: 'Error',
    //             detail: 'Unable to update project. See log for details',
    //             error: e,
    //             stack: e.stack
    //         });
    //         return;
    //     }
    // }

    setSelectedProject(selectedProject: Project|BetaReaderProject) {
        this.selectedProject = selectedProject;
        if (!this.selectedProject) {
            this.coreProject = undefined;
            return;
        }
        this.getCoreProjectFromAnyProject(this.selectedProject).then(p => {
            this.coreProject = p
            this.currentSchema = ProjectSchemas.SCHEMADISPLAY_BY_CODE[this.coreProject.schemaCode];
        });
    }

    getSelectedProject(): Project|BetaReaderProject {
        return this.selectedProject;
    }

    switchToEditLayout() {
        console.log('Switching to Edit from:', this.router.url);
        //window.location.href = window.location.protocol + '//' + window.location.host +
        //    '/#/content/'+this.getSelectedProject().uuid+'/'+encodeURIComponent(btoa(this.router.url));
        //alert(window.location.protocol + '//' + window.location.host +
        //    '/#/content/'+this.getSelectedProject().uuid+'/'+encodeURIComponent(btoa(this.router.url)));
        this.router.navigate(['/', 'content', this.getSelectedProject().uuid, encodeURI(btoa(this.router.url))]);
    }

    switchToBaseLayout() {
        console.log('Switching to Base at: ', this.redirectUrl);
        this.router.navigate([this.redirectUrl?this.redirectUrl:'/']);
    }

    switchToProfilePage() {
        console.log('Switching to Profile Page from:', this.router.url);
        this.router.navigate(['profile_page']);
    }

    private async getCoreProjectFromAnyProject(project: Project | BetaReaderProject): Promise<Project> {
        if (project instanceof BetaReaderProject) {
            return this.dataStore.betaReaderProjects
                .find(brp => brp.betaReaderProject.id === this.selectedProject.id)
                .project;
        } else if (project instanceof Project) {
            return project;
        } else {
            this.errorService.handleError({
                ancillaries: [project, this.selectedProject],
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to update project content - PROJECT TYPE IS UNHANDLED - Please report this. See log for details',
                stack: undefined
            });
            return null;
        }
    }


    async getProjectMatchedReaders() {
        for(let i = 0; i < this.dataStore.writerProjects.length; i++) {
            const result = await this.dataSource.getRepository(ProjectMatchType).createQueryBuilder("pmt").select("pmt").addSelect("platformUser.screenName").addSelect("platformUser.bioText")
                .leftJoinAndSelect("pmt.betaReader", "betaReader")
                .leftJoinAndSelect("betaReader.profileGroups", "profileGroups")
                .leftJoinAndSelect("profileGroups.profileSelections", "profileSelections")
                .leftJoinAndSelect("pmt.project", "project")
                .leftJoin("betaReader.platformUser", "platformUser")
                .where("project.id = :projectId", {projectId: this.dataStore.writerProjects[i].id})
                .andWhere("pmt.projectMatchStatus = :brra", {brra: ProjectMatchStatus.BETA_READER_REQUEST_ACCEPTANCE})
                .andWhere("project.lookingForBetaReaders = TRUE")
                .getMany();

            let tempArray: ProjectMatchType[] = result;

            tempArray.forEach((pmt: ProjectMatchType) => {
                this.dataStore.writerMatches.push(pmt);
            });
        }

        this.wMatchSignal.set(this.dataStore.writerMatches);
        console.log("HERE ARE THE PROJECTS TIED TO READERS INFO", this.dataStore.writerMatches);
        console.log(this.wMatchSignal());
    }

    async matchProjectToReader(inputGroup: ProjectProfileGroup) {

        this.progressService.blockWithMessage("Matching Projects");

        let idArray: string[] = [];
        let valueArray: string[] = [];
        let weightArray: number[] = [];
        //let badArray: string[] = [];
        //let requiredArray: string[] = [];
        let scoredQueryParts: string[] = [];
        //let badWhereQueryParts: string[] = [];
        //let reqWhereQueryParts: string[] = [];

        let rejectExists: boolean = false;
       

        for (let i = 0; i < inputGroup.profileSelections.length; i++) {
            if (inputGroup.profileSelections[i].value == "true") {

                if(inputGroup.projectSchemaCode == "FICTION_SERIES") {
                    for(let j = 0; j < FICTION_SERIES.projectDescriptorCategories.length; j++) {
                        if(inputGroup.profileSelections[i].categoryUniqueId == FICTION_SERIES.projectDescriptorCategories[j].uniqueId) {
                            if(FICTION_SERIES.projectDescriptorCategories[j].betaReaderMatchUse == BetaReaderMatchUse.GOOD_SCORE_ON_MATCH) {
                                idArray.push(inputGroup.profileSelections[i].descriptorUniqueId);
                                valueArray.push(inputGroup.profileSelections[i].value);
                                weightArray.push(FICTION_SERIES.projectDescriptorCategories[j].betaReaderMatchWeight);
                            }

                            /*if(FICTION_SERIES.projectDescriptorCategories[j].betaReaderMatchUse == BetaReaderMatchUse.MUST_NOT_MATCH_ANY){
                                badArray.push(inputGroup.profileSelections[i].descriptorUniqueId);
                            }

                            if(FICTION_SERIES.projectDescriptorCategories[j].betaReaderMatchUse == BetaReaderMatchUse.MUST_MATCH_AT_LEAST_ONE){
                                requiredArray.push(inputGroup.profileSelections[i].descriptorUniqueId);
                            }*/
                        } 
                    }
                } 
            }
        }

        for(let i = 0; i < idArray.length; i++) {
            scoredQueryParts[i] = "SUM(CASE WHEN profileSelections.descriptorUniqueId = '" + idArray[i]
                + "' AND profileSelections.value = '" + valueArray[i] 
                + "' THEN " + weightArray[i] + " ELSE 0 END)";
        }

        /*for(let i = 0; i < badArray.length; i++) {
            let tempString = "profileSelections.descriptorUniqueId != '" + badArray[i] + "'";

            badWhereQueryParts[i] = tempString;
        }

        for(let i = 0; i < requiredArray.length; i++) {
            let tempString = "profileSelections.descriptorUniqueId = " + requiredArray[i];

            reqWhereQueryParts[i] = tempString;
        }*/
     

        const scoredQuery: string = scoredQueryParts.join(' + ');
        //const mustNotMatch: string = badWhereQueryParts.join(' OR ');
        //const mustMatchOne: string = reqWhereQueryParts.join(' OR ');

        


        const query = this.dataSource.getRepository(Project).createQueryBuilder("proj").select()
            .addSelect(
                scoredQuery,
                "matchScore"
            )
            .setParameters({
                GENRE_PREFERENCE: FICTION_SERIES.projectDescriptorCategories[1].betaReaderMatchWeight,
                LENGTH: FICTION_SERIES.projectDescriptorCategories[2].betaReaderMatchWeight,
                AGE: FICTION_SERIES.projectDescriptorCategories[3].betaReaderMatchWeight,
                TRIGGER_WARNINGS: -1 * (FICTION_SERIES.projectDescriptorCategories[4].betaReaderMatchWeight),
            })
            .leftJoin("proj.profileGroup", "profileGroup")
            .leftJoin("profileGroup.profileSelections", "profileSelections")
            //.leftJoin("", "projectMatchType")
            .where("profileSelections.value = 'true'")
            //.orWhere("projectMatchType.project_match_status = 'Active'")
            
            for(let i = 0; i < inputGroup.profileSelections.length; i++) {
                for(let j = 0; j < FICTION_SERIES.projectDescriptorCategories.length; j++)
                    if (inputGroup.profileSelections[i].categoryUniqueId == FICTION_SERIES.projectDescriptorCategories[j].uniqueId && inputGroup.profileSelections[i].value == 'true') {
                        switch (FICTION_SERIES.projectDescriptorCategories[j].betaReaderMatchUse) {
                            case BetaReaderMatchUse.MUST_NOT_MATCH_ANY: 
                                //query.andWhere("profileSelections.descriptorUniqueId != :badId AND profileSelections.value = 'true'", {badId: inputGroup.profileSelections[i].descriptorUniqueId});

                                query.addCommonTableExpression(
                                    `
                                        SELECT p.id 
                                        FROM project p
                                        LEFT JOIN project_profile_group ppg ON p.profile_group_id = ppg.id
                                        LEFT JOIN project_selection ps ON ppg.id = ps.profile_group_id
                                        WHERE ps.descriptor_unique_id = :badId AND ps.value = 'true'
                                    `, 
                                    "rejected_projects"
                                );
                                query.setParameters({badId: inputGroup.profileSelections[i].descriptorUniqueId});
                                rejectExists = true;
                                break;
                            
                            case BetaReaderMatchUse.MUST_MATCH_AT_LEAST_ONE:
                                query.andWhere("profileSelections.descriptorUniqueId = :reqId AND profileSelections.value = 'true'", {reqId: inputGroup.profileSelections[i].descriptorUniqueId});
                                break;

                            default:
                                break;
                        }
                    }
            }

            if (rejectExists) {
                query.leftJoin(
                    "rejected_projects",
                    "rj",
                    "proj.id = rj.id"    
                )
                .andWhere("rj.id IS NULL");
            }
            query.groupBy("proj.id");
            
            const result = await query.getRawAndEntities();

        console.log(result);

        this.dataStore.readerMatchedProjects = result.entities;
        console.log("READER MATCHED ARRAY", this.dataStore.readerMatchedProjects);

        for(let i = 0; i < this.dataStore.readerMatchedProjects.length; i++) {
            const tempMatch = await this.getReaderProjectMatchType(this.dataStore.readerMatchedProjects[i]);
            this.dataStore.betaMatches[i] = tempMatch;
        }

        this.getReaderActiveProjects();

        console.log("BETA MATCHES ARRAY", this.dataStore.betaMatches);


        
        this.rMatchSignal.set(this.dataStore.betaMatches);
        this.progressService.unBlock();
    }

    getMatchArray() {
        return this.dataStore.readerMatchedProjects;
    }

    async getReaderActiveProjects() {
        const result = await this.dataSource.getRepository(ProjectMatchType).createQueryBuilder("pmt")
            .leftJoinAndSelect("pmt.project", "project").leftJoinAndSelect("project.profileGroup", "profileGroup").leftJoinAndSelect("profileGroup.profileSelections", "profileSelections")
            .leftJoinAndSelect("pmt.betaReader", "betaReader")
            .where("pmt.projectMatchStatus = 'Active'")
            .andWhere("betaReader.id = :betaReaderId", {betaReaderId: this.dataStore.betaReader.id})
            .getMany();

            result.forEach((pmt: ProjectMatchType) => {
                let exists: boolean = false;
                for(let i = 0; i < this.dataStore.betaMatches.length; i++) {
                    if(this.dataStore.betaMatches[i].id == pmt.id) {
                        exists = true;
                    }
                }
                if(!exists) {
                    this.dataStore.betaMatches.push(pmt);
                }
            });
    }

    async getReaderProjectMatchType(project: Project): Promise<ProjectMatchType> {
        
        

        const result = await this.dataSource.getRepository(ProjectMatchType).createQueryBuilder("pmt").select("pmt")
            .leftJoinAndSelect("pmt.project", "project").leftJoinAndSelect("project.profileGroup", "profileGroup").leftJoinAndSelect("profileGroup.profileSelections", "profileSelections")
            .leftJoinAndSelect("pmt.betaReader", "betaReader")
            .where("project.id = :projectId", {projectId: project.id}).andWhere("betaReader.id = :betaReaderId", {betaReaderId: this.dataStore.betaReader.id})
            .getOne();
        
        console.log("RESULT MATCH TYPE", result);
        
        if (!result) {
            console.log("No match type exists, inserting");
            let tempMatch: ProjectMatchType = {
                id: null,
                projectMatchStatus: ProjectMatchStatus.MATCHED,
                matchScore: 0,
                project: project,
                betaReader: this.dataStore.betaReader
            };
            const pmtInsert = await this.dataSource.getRepository(ProjectMatchType).createQueryBuilder("pmtI").insert()
            .into(ProjectMatchType)
            .values({
            projectMatchStatus: ProjectMatchStatus.MATCHED,
            //this should use a value passed down from the calculation, but currently uses 0 just for testing
            matchScore: 0,
            project: project,
            betaReader: this.dataStore.betaReader
           })
           .execute();
           
           tempMatch.id = Number.parseInt(pmtInsert.raw.rows[0].id, 10);

           const projectQuery = await this.dataSource.getRepository(Project).createQueryBuilder("project")
           .leftJoinAndSelect("project.profileGroup", "profileGroup").leftJoinAndSelect("profileGroup.profileSelections", "profileSelections")
           .where("project.id = :projectId", {projectId: project.id})
           .getOne();

           tempMatch.project.profileGroup = projectQuery.profileGroup;
            
            return tempMatch;
        }



        return result;
        
        
        
    }

    async getBRPByProjectId(project: Project) {
        let result = await this.dataSource.getRepository(BetaReaderProject).createQueryBuilder("brp").select("brp").where("brp.project = :proj", {proj: project}).getOne();

        return result;
    }

    async setProjectMatchTypeStatus(projectMatch: ProjectMatchType, status: ProjectMatchStatus) {
        try {
            const result = await this.dataSource.getRepository(ProjectMatchType).update({ id: projectMatch.id},
                {
                    projectMatchStatus: status.valueOf()
                }
            );
        } catch (e) {
            this.errorService.handleError({
                error: e,
                stack: e.stack,
                ancillaries: [uuid, status],
                severity: 'error',
                summary: 'Error',
                detail: 'Unable to change Project Match Status for project. Check log for details.'
            });
        }
    }

    getPMTByProject(proj: Project) {
        if (this.dataStore.betaMatches) {
            for(const p of this.dataStore.betaMatches) {
                if (proj.id == p.project.id) {
                    return p;
                }
            }
        }
        return null;
    }

}

export type DisplayCategoryType = {
    enUsLabel: string,
    color: string,
    icon: string,
    value: string | string[]
};

/*export type ProjectMatchType = {
    author: string,
    title: string,
    score: number,
    categories: DisplayCategoryType[],
    projectMatchStatus: ProjectMatchStatus,
    betaReaderProject: BetaReaderProject,
    betaReader: BetaReaderProfile,
    project: Project
};*/

export enum WriterResourceType {
    BETA_READER = "Beta Reader",
    LINE_EDITOR = "Line Editor"
};

export type WriterResourceMatchType = {
    resourceType: WriterResourceType.BETA_READER,
    name: string,
    bioSketch: string,
    project: BetaReaderProject
};// Pipe delim list of resource types with their correct project type - like above| ;

export class RoleProject {
    public project: Project;
    public role: PlatformRole;
    public roleProject: null|BetaReaderProject; //null for Author
    public colorIndex: number;
}

export const cognitoAuthGuard: CanActivateFn = async (route, state) => {
    // console.log("Running canActivate check");
    const dataService = inject(DataService);
    if (dataService.started() && dataService.loggedIn()) {
        return true;
    } else {
        console.log("User not logged in, redirecting to login page: started: " + dataService.started() + " loggedIn: " + dataService.loggedIn());
        return inject(Router).parseUrl("/auth/login");
    }
};
